import React, { useMemo } from 'react';
import { Menu } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    DashboardOutlined,
    UserOutlined,
    ShoppingOutlined,
    FileTextOutlined,
    QuestionCircleOutlined,
    TeamOutlined,
    SettingOutlined,
    FileDoneOutlined,
    IdcardOutlined,
    BarChartOutlined,
    HistoryOutlined,
} from '@ant-design/icons';
import { ROUTES } from '../../constants/PathRoutes';
import { PERMISSIONS } from '../../constants/permissions';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

export default function AdminMenu({ mode = 'inline', onItemClick, ...props }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { hasPermission } = useAdminAuth();

    const handleMenuClick = (path) => {
        navigate(path);
        onItemClick?.();
    };

    const allMenuItems = [
        {
            key: ROUTES.ADMIN_DASHBOARD,
            icon: <DashboardOutlined />,
            label: 'Dashboard',
            onClick: () => handleMenuClick(ROUTES.ADMIN_DASHBOARD),
            permission: null, // Todos têm acesso
        },
        {
            key: ROUTES.ADMIN_CLIENT_LIST,
            icon: <IdcardOutlined />,
            label: 'Clientes',
            onClick: () => handleMenuClick(ROUTES.ADMIN_CLIENT_LIST),
            permission: PERMISSIONS.CLIENT_MANAGE,
        },
        {
            key: ROUTES.ADMIN_USUARIOS,
            icon: <UserOutlined />,
            label: 'Usuários',
            onClick: () => handleMenuClick(ROUTES.ADMIN_USUARIOS),
            permission: PERMISSIONS.USER_MANAGE,
        },
        {
            key: 'ecommerce',
            icon: <ShoppingOutlined />,
            label: 'Ecommerce',
            permission: PERMISSIONS.ECOMMERCE_MANAGE,
            children: [
                {
                    key: ROUTES.ADMIN_ECOMMERCE_CATEGORY_LIST,
                    label: 'Categorias',
                    onClick: () => handleMenuClick(ROUTES.ADMIN_ECOMMERCE_CATEGORY_LIST),
                },
                {
                    key: ROUTES.ADMIN_ECOMMERCE_PRODUCT_LIST,
                    label: 'Produtos',
                    onClick: () => handleMenuClick(ROUTES.ADMIN_ECOMMERCE_PRODUCT_LIST),
                },
                {
                    key: ROUTES.ADMIN_ECOMMERCE_SHIPPING_LIST,
                    label: 'Fretes',
                    onClick: () => handleMenuClick(ROUTES.ADMIN_ECOMMERCE_SHIPPING_LIST),
                },
                {
                    key: ROUTES.ADMIN_ECOMMERCE_ORDER_LIST,
                    label: 'Pedidos',
                    onClick: () => handleMenuClick(ROUTES.ADMIN_ECOMMERCE_ORDER_LIST),
                },
            ],
        },
        {
            key: ROUTES.ADMIN_BLOG_LIST,
            icon: <FileTextOutlined />,
            label: 'Blog',
            onClick: () => handleMenuClick(ROUTES.ADMIN_BLOG_LIST),
            permission: PERMISSIONS.BLOG_MANAGE,
        },
        {
            key: ROUTES.ADMIN_FAQ_LIST,
            icon: <QuestionCircleOutlined />,
            label: 'FAQ',
            onClick: () => handleMenuClick(ROUTES.ADMIN_FAQ_LIST),
            permission: PERMISSIONS.FAQ_MANAGE,
        },
        {
            key: ROUTES.ADMIN_ECOMMERCE_PRESCRIPTION_LIST,
            icon: <FileDoneOutlined />,
            label: 'Prescrições',
            onClick: () => handleMenuClick(ROUTES.ADMIN_ECOMMERCE_PRESCRIPTION_LIST),
            permission: PERMISSIONS.PRESCRIPTION_MANAGE,
        },
        {
            key: ROUTES.ADMIN_STAFF_LIST,
            icon: <TeamOutlined />,
            label: 'Equipe',
            onClick: () => handleMenuClick(ROUTES.ADMIN_STAFF_LIST),
            permission: PERMISSIONS.STAFF_MANAGE,
        },
        {
            key: 'reports',
            icon: <BarChartOutlined />,
            label: 'Relatórios',
            permission: PERMISSIONS.REPORTS_VIEW,
            children: [
                {
                    key: ROUTES.ADMIN_REPORTS_PATIENTS,
                    label: 'Pacientes',
                    onClick: () => handleMenuClick(ROUTES.ADMIN_REPORTS_PATIENTS),
                },
                {
                    key: ROUTES.ADMIN_REPORTS_ECOMMERCE,
                    label: 'Ecommerce',
                    onClick: () => handleMenuClick(ROUTES.ADMIN_REPORTS_ECOMMERCE),
                },
                {
                    key: ROUTES.ADMIN_REPORTS_GENERATED,
                    label: 'Relatórios Gerados',
                    onClick: () => handleMenuClick(ROUTES.ADMIN_REPORTS_GENERATED),
                },
            ],
        },
        {
            key: ROUTES.ADMIN_SITE_SETTINGS,
            icon: <SettingOutlined />,
            label: 'Configurações',
            onClick: () => handleMenuClick(ROUTES.ADMIN_SITE_SETTINGS),
            permission: PERMISSIONS.SITE_SETTINGS_MANAGE,
        },
        {
            key: ROUTES.ADMIN_AUDIT_LOG_LIST,
            icon: <HistoryOutlined />,
            label: 'Logs de Auditoria',
            onClick: () => handleMenuClick(ROUTES.ADMIN_AUDIT_LOG_LIST),
            permission: PERMISSIONS.AUDIT_LOG_VIEW,
        },
    ];

    const menuItems = useMemo(() => {
        return allMenuItems
            .filter(item => !item.permission || hasPermission(item.permission))
            .map(({ permission, ...item }) => item);
    }, [hasPermission]);

    const defaultOpenKeys = useMemo(() => {
        if (location.pathname.startsWith('/admin/auth/ecommerce')) {
            return ['ecommerce'];
        }
        if (location.pathname.startsWith('/admin/auth/relatorios')) {
            return ['reports'];
        }
        return [];
    }, [location.pathname]);

    return (
        <Menu
            mode={mode}
            selectedKeys={[location.pathname]}
            defaultOpenKeys={defaultOpenKeys}
            items={menuItems}
            {...props}
        />
    );
}